<template>
  <div>
    <SectionSeparatorWrapper headerText="Policy holder" :separator="false" headerRequiredFieldsText="indicates required field">
      <FormField labelText="Start date of change" required>
        <p class="disabled-input half-width" aria-label="Start date of change">{{ reverseFormatFormDate(policyHolder.startDate) }}</p>
      </FormField>

      <SectionSeparatorWrapper :separator="false">
        <h3 class="h6 sub-heading-underlined">Member detail</h3>
        <FormField :helperText="referenceData.additionalInformationUpdateDetails.informationText" :noLabelOverride="true" :required="false">
          <MemberTag
            class="member-details-section"
            :firstName="policyHolder.firstName"
            :lastName="policyHolder.lastName ? policyHolder.lastName : '&#8212;'"
            relationship="policy holder"
            :showLastName="true"
          />
        </FormField>
        <!-- Over coverage -->
        <FormField v-if="changedKidsOnlyToOtherComposition">
          <ApiLoading v-if="apiStatus.overCoverage.loading || apiStatus.overCoverage.fail" :apiError="apiStatus.overCoverage.fail" class="api-loading">
            <p>
              Sorry, we couldn’t load the information.
              <button class="non-button-style" @click="getOverCoverageInfo">Reload</button>
              the result to continue.
            </p>
          </ApiLoading>
          <Alert v-else-if="isPolicyHolderOverCovered" alertType="error" :message="OVER_COVERAGE_ERROR_MESSAGE" :isPlainText="false"></Alert>
        </FormField>
      </SectionSeparatorWrapper>
      <SectionSeparatorWrapper v-if="hasAddressStateChanged" :separator="false">
        <div class="form-section">
          <h3 class="h6 sub-heading-underlined">Address</h3>
          <AddressValidator
            @updateAddressDetails="updateAddressDetail"
            @updateAddressValidation="updateAddressValidation"
            :preFilledResidentialAddress="policyHolder.residentialAddress"
            :preFilledResidentialState="changeCoverData.step1.state"
            :preFilledPostalAddress="policyHolder.postalAddress"
            :preFilledPOBox="policyHolder.POBox"
            :preFillledDifferentPostalAddress="policyHolder.differentPostalAddress"
            :stateLocked="true"
            :disableField="!hasAddressStateChanged"
            :isChangeCover="true"
            :isExistingMember="!hasAddressStateChanged"
            :showResidentialAlert="hasAddressStateChanged && multiplePolicyHolderMemberships"
            :showPostalAlert="postalAddressUpdated && (multiplePolicyHolderMemberships || multipleMembershipsAndAtLeastOnePolicyHolder)"
            :postalAddressHelpText="referenceData.postalAddressHelpText.informationText"
            showPostalAddressHelperText
          ></AddressValidator>
        </div>
      </SectionSeparatorWrapper>

      <!-- Transferring from another fund -->
      <SectionSeparatorWrapper v-if="showTransferOtherFundQuestion" :separator="false" class="field">
        <div class="form-section">
          <h3 class="h6 sub-heading-underlined">Transferring from another fund</h3>
          <FormField labelText="Are you transferring from another fund?" :helperText="referenceData.additionalInformationTransferringFundHistory.informationText">
            <ul class="radio-group large">
              <li v-for="option in displayRadioOptions" :key="`from-other-funds-policyholder-${option.display}`">
                <input
                  type="radio"
                  disabled
                  :id="`from-other-funds-policyholder-${option.display}`"
                  v-model.lazy="policyHolder.transferFromOthers"
                  :name="`from-other-funds-policyholder-${option.display}`"
                  :value="option.returnValue"
                />
                <label :for="`from-other-funds-policyholder-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
        </div>
        <template v-if="policyHolder.transferFromOthers">
          <div class="form-section" v-for="(fund, index) in policyHolder.selectedInsuranceFund" :key="index">
            <FormField labelText="Which fund are you transferring from?" required class="form-section">
              <DropdownSelect
                :items="fundNamesNewPerson"
                v-model="fund.name"
                name="policyholder-fund-name"
                :id="`policyholder-new-fundName-${index}`"
                :initialSelect="getDropdownOption(fundNamesNewPerson, fund.name, 'Please select a health fund')"
              />
            </FormField>
            <FormField
              labelText="What was your member number at your previous health fund?"
              placeholder="XXXXXXXXX"
              type="text"
              class="form-section"
              :id="fund.number"
              v-model.lazy="fund.number"
              required
              :error="v$.policyHolder.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number?.length > 0"
              :errorMessage="getFundErrorMessage(v$.policyHolder.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number || [])"
            />
            <FormField>
              <p v-html="informationReferenceData.insuranceFundInformationText.informationText" />
            </FormField>

            <div v-if="v$.policyHolder.selectedInsuranceFund.$model.length > 1" class="form-section">
              <a @click.prevent="removeSelectField(policyHolder.selectedInsuranceFund, index)" class="always-fresh" href="#" role="button">Remove fund</a>
            </div>
          </div>

          <div class="form-section">
            <a
              v-if="v$.policyHolder.selectedInsuranceFund.$model.length < 2"
              @click.prevent="addAnotherSelectField(policyHolder.selectedInsuranceFund)"
              role="button"
              class="always-fresh"
              href="#"
            >
              Add another fund to switch from
            </a>
          </div>
        </template>
      </SectionSeparatorWrapper>

      <!-- Veteran card -->
      <SectionSeparatorWrapper v-if="hasEligibilityChanged && isExServingOrServing" :separator="false">
        <div class="form-section">
          <h3 class="h6 sub-heading-underlined">Veteran card</h3>
          <FormField labelText="Do you have a Veteran card?" :helperText="referenceData.additionalInformationUpdateVeteranDetails.informationText">
            <ul class="radio-group large">
              <li v-for="option in displayRadioOptions" :key="`policy-holder-veteran-card-${option.display}`">
                <input
                  type="radio"
                  disabled
                  :id="`policy-holder-veteran-card-${option.display}`"
                  v-model.lazy="policyHolder.veteranCard.haveVeteranCard"
                  :name="`policy-holder-veteran-card-${option.display}`"
                  :value="option.returnValue"
                  @change="haveVeteranCardUpdated"
                />
                <label :for="`policy-holder-veteran-card-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
        </div>
        <FormField
          v-if="policyHolder.veteranCard.haveVeteranCard"
          :required="policyHolder.veteranCard.haveVeteranCard"
          labelText="Which Veteran card do you hold?"
          class="form-section"
        >
          <ul class="radio-group large form-section veteran-card-type">
            <li v-for="option in informationReferenceData.veteranCardTypeOptions" :key="option.returnValue">
              <input
                type="radio"
                disabled
                :id="option.returnValue"
                :value="option.returnValue"
                name="veteranCardPolicyHolder"
                v-model.lazy="policyHolder.veteranCard.veteranCardType"
                @change="veteranCardTypeUpdated"
              />
              <label :for="option.returnValue">{{ option.display }}</label>
            </li>
          </ul>
          <template v-if="v$.policyHolder.veteranCard.veteranCardType.$model === 'Veteran White Card'">
            <p class="form-section" v-html="informationReferenceData.veteranCardConditionInformationText.informationText" />
            <MultiSelect
              name="white card options"
              id="veteranCardConditionOptionsPolicyHolder"
              :items="informationReferenceData.veteranCardConditionOptions"
              @updateData="updateVeteranCardOptions"
              :selectedItems="policyHolder.veteranCard.veteranCardConditionOptions"
              placeholderText="Search for a listed accepted condition"
              ref="policyHolder.veteranCardConditionOptions"
              :maximum="informationReferenceData.veteranCardConditionOptions.length"
            ></MultiSelect>
          </template>
        </FormField>
      </SectionSeparatorWrapper>

      <!-- Concession card -->
      <SectionSeparatorWrapper v-if="(hasAddressStateChanged || hasEligibilityChanged) && showConcessionCardSection()" :separator="false">
        <div class="form-section">
          <h3 class="h6 sub-heading-underlined">Concession card</h3>
          <FormField labelText="Do you have a concession card?">
            <ul class="radio-group large">
              <li v-for="option in displayRadioOptions" :key="`concession-card-${option.display}`">
                <input
                  type="radio"
                  :id="`concession-card-${option.display}`"
                  v-model.lazy="policyHolder.haveConcessionCard"
                  :name="`concession-card-${option.display}`"
                  :value="option.returnValue"
                  @change="!policyHolder.haveConcessionCard"
                />
                <label :for="`concession-card-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
        </div>
        <template v-if="showConcessionCardDetailsForm()">
          <div class="form-section" v-for="(concession, index) in policyHolder.selectedConcessionCardOptions" :key="index">
            <FormField>
              <p>
                Advising us whether you hold a concession card ensures we are not paying for services that you are already entitled to, which helps us keep premiums more
                affordable.
              </p>
            </FormField>
            <FormField class="form-section" labelText="Which concession card do you hold?">
              <DropdownSelect
                initialSelect="Please select a concession card"
                :items="getSelectOptionsLeft(index, policyHolder.selectedConcessionCardOptions, informationReferenceData.concessionCardOptions)"
                v-model="concession.name"
                @input="cardDetailUpdated(index, cardDetailUpdatedType.TYPE)"
              />
            </FormField>
            <FormField
              labelText="Card number"
              placeholder="XXXXXXXXX"
              type="text"
              class="form-section"
              v-model.lazy="concession.number"
              :error="v$.policyHolder.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.number?.length > 0"
              :errorMessage="getConcessionNumberErrorMessage(v$.policyHolder.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.number || [])"
              @input="cardDetailUpdated(index, cardDetailUpdatedType.NUMBER)"
            />
            <FormField
              labelText="Expiry date"
              type="text"
              class="form-section half-width"
              v-model.lazy="concession.expire"
              placeholder="DD / MM / YYYY"
              v-maska="'## / ## / ####'"
              :required="false"
              :error="v$.policyHolder.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.expire?.length > 0"
              :errorMessage="getConcessionExpiryErrorMessage(v$.policyHolder.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.expire || [])"
              @input="cardDetailUpdated(index, cardDetailUpdatedType.EXPIRY_DATE)"
            />
            <div v-if="v$.policyHolder.selectedConcessionCardOptions.$model.length > 1" class="form-section">
              <a @click.prevent="removeSelectField(policyHolder.selectedConcessionCardOptions, index)" class="always-fresh" role="button" href="#">Remove card</a>
            </div>
            <div class="form-section">
              <a
                v-if="v$.policyHolder.selectedConcessionCardOptions.$model.length < informationReferenceData.concessionCardOptions.length"
                @click.prevent="addAnotherSelectField(policyHolder.selectedConcessionCardOptions)"
                role="button"
                class="non-button-style"
                href="#"
              >
                Add another concession card
              </a>
            </div>
          </div>
        </template>
      </SectionSeparatorWrapper>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import { helpers, requiredIf, maxLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { DATE_VALIDATOR } from '@general/js/validators';
import { getCurrentDate, invalidConcessionCardNumber, isPastDateIncludingToday, reverseFormatFormDate, stringWithSymbols } from '@general/js/utils';
import { showConcessionCardSection as showConcessionCard, showConcessionCardDetailsForm as showConcessionCardDetails } from '@/utils';
import { Alert, ApiLoading, FormField, MemberTag, SectionSeparatorWrapper } from '@/components';
import AddressValidator from '@general/js/components/addressValidation.vue';
import { MultiSelect, DropdownSelect } from '@general/js/components/';
import { ConcessionCardTypes, OperationType, VeteranCardTypeOptions } from '@/enums';
import { NeedsAnalysisEligibility, CoverTypeValue, CoverComposition, LHCOverruleReasons } from '@general/js/enums';
import Services from '@/services/MyCover/ChangeCover/ChangeProduct';
import CaptureInformationMixins from '../CaptureInformationMixins';
import ChangeCoverMixins from '../ChangeCoverMixins';

export default {
  name: 'PolicyHolder',

  setup() { return { v$: useVuelidate() }; },

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    AddressValidator,
    Alert,
    ApiLoading,
    FormField,
    MemberTag,
    MultiSelect,
    SectionSeparatorWrapper,
    DropdownSelect,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: true,
    },
    // Fund names using `dhl_hiccode` when adding a new partner
    fundNamesNewPerson: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      policyHolder: {
        startDate: null,
        firstName: null,
        middleName: null,
        lastName: null,
        residentialAddress: {},
        addressesInvalid: false,
        postalAddress: {},
        differentPostalAddress: false,
        POBox: false,
        veteranCard: {
          haveVeteranCard: null,
          veteranCardType: '',
          veteranCardConditionOptions: [],
        },
        haveConcessionCard: null,
        selectedConcessionCardOptions: [{ name: '', number: '', expire: '' }],
        abd: null,
        transferFromOthers: null,
        selectedInsuranceFund: [{ name: '', number: '' }],
      },
      abdLhcResponseValid: false, // default
      eligibleForLHC: false,
      parentClassName: 'policyHolder',
      displayRadioOptions: [
        { returnValue: true, display: 'Yes' },
        { returnValue: false, display: 'No' },
      ],
      isActive: false,
      cardDetailUpdatedType: {
        TYPE: 'cardType',
        NUMBER: 'cardNumber',
        EXPIRY_DATE: 'expiryDate',
      },
      isPolicyHolderOverCovered: false,
      apiStatus: {
        overCoverage: {
          fail: false,
          loading: false,
        },
      },
    };
  },

  async beforeMount() {
    this.policyHolder = { ...this.policyHolder, ...this.policyHolderStep, startDate: getCurrentDate() };

    if (this.changedKidsOnlyToOtherComposition) {
      this.getOverCoverageInfo();
    }

    if (this.changeCoverData.step2?.policyHolder?.lhc?.overruledReason) {
      this.policyHolder.transferFromOthers = this.changeCoverEnteredData.step2.policyHolder.lhc?.overruledReason === LHCOverruleReasons.CURRENT_HOSPITAL_COVER;
    }

    if (this.changeCoverData.step2.policyHolder?.veteranCard?.haveVeteranCard !== null) {
      this.policyHolder.veteranCard = { ...this.changeCoverData.step2.policyHolder.veteranCard };

      if (this.policyHolder.veteranCard.veteranCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER) {
        this.policyHolder.veteranCard.veteranCardConditionOptions =
          this.changeCoverData.step2.policyHolder.veteranCard.veteranCardConditionOptions?.map((condition) => {
            return {
              ...condition,
              display: this.getVeteranCardDisplayValue(this.captureInformationReferenceData.veteranCardConditionOptions, condition.returnValue),
            };
          }) ?? [];
      }
      this.veteranCardTypeUpdated();
    }
  },

  activated() {
    this.isActive = true;

    // ensure that validation is triggered (because there could be prefilled information)
    this.v$.policyHolder.veteranCard.veteranCardType.$touch();
    if (this.policyHolderStep.haveConcessionCard) {
      this.v$.policyHolder.selectedConcessionCardOptions.$touch();
    }
  },

  deactivated() {
    this.isActive = false;
  },

  watch: {
    v$: {
      handler(newData) {
        let isInvalid;
        if (this.changedKidsOnlyToOtherComposition) {
          isInvalid = this.apiStatus.overCoverage.fail || this.apiStatus.overCoverage.loading || !!this.isPolicyHolderOverCovered || newData.$invalid || newData.$error;
        } else {
          isInvalid = newData.$invalid || newData.$error;
        }

        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('formUpdated', {
              response: this.policyHolder,
              isInvalid,
            });
          });
        }
      },
      deep: true,
    },
    isPolicyHolderOverCovered(val) {
      this.$emit('formUpdated', {
        response: this.policyHolder,
        isInvalid: this.apiStatus.overCoverage.loading || this.apiStatus.overCoverage.fail || !!val || this.v$.$invalid || this.v$.$error,
      });
    },
    'policyHolder.haveConcessionCard': function () {
      if (!(this.hasAddressStateChanged || this.hasEligibilityChanged)) {
        return;
      }

      // If concession card details form is not visible based on business rules, make it a blank array
      if (!this.showConcessionCardDetailsForm()) {
        this.policyHolder.selectedConcessionCardOptions = [];
      } else if (this.initialData.policyHolder.selectedConcessionCardOptions.length > 0) {
        this.policyHolder.selectedConcessionCardOptions = this.showValidConcessionCards();
      } else {
        this.policyHolder.selectedConcessionCardOptions = [{ name: '', number: '', expire: '' }];
      }
    },
  },

  validations: {
    policyHolder: {
      addressesInvalid: {
        required: requiredIf(function () {
          return this.hasAddressStateChanged;
        }),
        isValid(bool) {
          return bool === false;
        },
      },
      veteranCard: {
        veteranCardType: {
          required: requiredIf(function () {
            if (this.hasEligibilityChanged && this.isExServingOrServing) {
              // only required if I/partner has/is serving
              if (this.policyHolder.veteranCard.haveVeteranCard === null) {
                return true;
              }
              return this.policyHolder.veteranCard.haveVeteranCard;
            }
            return false;
          }),
        },
        veteranCardConditionOptions: {
          areConditionsSelected(selectedOptions) {
            // For white veteran card, at least 1 accepted condition should be selected
            if (
              this.policyHolder.veteranCard.haveVeteranCard === true &&
              this.policyHolder.veteranCard.veteranCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER &&
              this.hasEligibilityChanged &&
              this.isExServingOrServing
            ) {
              return selectedOptions.length > 0;
            }
            return true;
          },
        },
      },
      selectedConcessionCardOptions: {
        $each: helpers.forEach({
          name: {
            required: requiredIf(function () {
              if (!(this.hasAddressStateChanged || this.hasEligibilityChanged)) {
                return false;
              }
              if (this.showConcessionCardSection()) {
                if (this.policyHolder.haveConcessionCard === null) {
                  return true;
                }
              }
              return this.showConcessionCardDetailsForm();
            }),
          },
          number: {
            required: requiredIf(function () {
              if (!(this.hasAddressStateChanged || this.hasEligibilityChanged)) {
                return false;
              }
              if (this.showConcessionCardSection()) {
                if (this.policyHolder.haveConcessionCard === null) {
                  return true;
                }
              }
              return this.showConcessionCardDetailsForm();
            }),
            invalidConcessionCardNumber: (value, option) => {
              if (option.name === ConcessionCardTypes.PCC || option.name === ConcessionCardTypes.HCC || option.name === ConcessionCardTypes.CSHC) {
                // TODO: Add these checks to ConcessionUtils file
                return invalidConcessionCardNumber(value);
              }
              return true;
            },
          },
          expire: {
            isPastDate: (value, option) => {
              if (value !== '' && (option.name === ConcessionCardTypes.PCC || option.name === ConcessionCardTypes.HCC || option.name === ConcessionCardTypes.CSHC)) {
                // TODO: Add these checks to concessionUtils file
                return !isPastDateIncludingToday(value);
              }
              return true;
            },
            IS_INVALID: (value) => {
              return DATE_VALIDATOR(value);
            },
          },
        }),
      },
      selectedInsuranceFund: {
        $each: helpers.forEach({
          name: {
            required: requiredIf(function () {
              if (this.policyHolder.transferFromOthers === null && this.showTransferOtherFundQuestion) {
                return true;
              }
              return this.policyHolder.transferFromOthers;
            }),
          },
          number: {
            required: requiredIf(function () {
              if (this.policyHolder.transferFromOthers === null && this.showTransferOtherFundQuestion) {
                return true;
              }
              return this.policyHolder.transferFromOthers;
            }),
            stringWithSymbols,
            maxLength: maxLength(19),
          },
        }),
      },
    },
  },

  computed: {
    hasAddressStateChanged() {
      return this.currentState !== this.changeCoverData.step1.state;
    },

    hasEligibilityChanged() {
      return this.currentEligibility !== this.changeCoverData.step1.eligibility;
    },

    isExServingOrServing() {
      if (this.changeCoverData.step1.eligibility === NeedsAnalysisEligibility.SERVING || this.changeCoverData.step1.eligibility === NeedsAnalysisEligibility.EX_SERVING) {
        return true;
      }
      return false;
    },

    changedKidsOnlyToOtherComposition() {
      // Only from ChildrenOnly to any other composition, need to check whether the policy holder is over covered with the change.
      if (
        this.currentComposition.toLowerCase() === CoverComposition.CHILDREN_ONLY.toLowerCase() &&
        this.changeCoverData.step2.composition.toLowerCase() !== CoverComposition.CHILDREN_ONLY.toLowerCase()
      ) {
        return true;
      }

      return false;
    },

    showTransferOtherFundQuestion() {
      return this.changeCoverEnteredData.step2.policyHolder.lhc?.overruledReason === LHCOverruleReasons.CURRENT_HOSPITAL_COVER;
    },

    postalAddressUpdated() {
      const initialPostalAddress = this.initialData.policyHolder.postalAddress ?? {};
      // Check if any field in the postal address has changed
      return !isEqual(initialPostalAddress, this.policyHolder.postalAddress);
    },
  },

  methods: {
    resetVeteranCardDetails() {
      this.policyHolder.veteranCard.veteranCardType = '';

      if (this.policyHolder.veteranCard.veteranCardConditionOptions.length > 0) {
        this.policyHolder.veteranCard.veteranCardConditionOptions = [];
      }
    },

    haveVeteranCardUpdated() {
      const initialVeteranCard = this.initialData.policyHolder.veteranCard;
      const newVeteranCard = this.policyHolder.veteranCard;

      if (initialVeteranCard.haveVeteranCard === true) {
        if (newVeteranCard.haveVeteranCard === false) {
          initialVeteranCard.op = OperationType.REMOVE;
        } else {
          initialVeteranCard.op = OperationType.NONE;
        }
      }
    },

    veteranCardTypeUpdated() {
      const initialCardType = this.initialData.policyHolder.veteranCard.veteranCardType;
      const targetCardType = this.policyHolder.veteranCard.veteranCardType;

      if (this.initialData.policyHolder.veteranCard.haveVeteranCard) {
        if (initialCardType !== targetCardType) {
          this.initialData.policyHolder.veteranCard.op = OperationType.REMOVE;
          this.policyHolder.veteranCard.op = OperationType.ADD;
          this.policyHolder.veteranCard.startDate = getCurrentDate();
        } else {
          const isWhiteVeteranCard = initialCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER && targetCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER;
          const hasChangedConditions = this.policyHolder.veteranCard.veteranCardConditionOptions.some((condition) => {
            return condition.op !== OperationType.NONE;
          });

          // if any accepted condition has been changed, send operation as replace
          if (isWhiteVeteranCard && hasChangedConditions) {
            this.policyHolder.veteranCard.op = OperationType.REPLACE;
          } else {
            this.initialData.policyHolder.veteranCard.op = OperationType.NONE;
            this.policyHolder.veteranCard.op = OperationType.NONE;
            this.policyHolder.veteranCard.startDate = this.initialData.policyHolder.veteranCard.startDate;
          }
        }
      } else {
        this.policyHolder.veteranCard.op = OperationType.ADD;
        this.policyHolder.veteranCard.startDate = getCurrentDate();
      }
    },

    updateVeteranCardOptions(data) {
      const existingAcceptedConditions = cloneDeep(this.initialData.policyHolder.veteranCard.veteranCardConditionOptions);
      const acceptedConditions = [];

      // If there are no current accepted conditions selected
      if (data.length === 0 && existingAcceptedConditions) {
        this.initialData.policyHolder.veteranCard.veteranCardConditionOptions.forEach((condition) => {
          condition.op = OperationType.REMOVE;
        });
      }
      // if there are accepted conditions selected
      else {
        if (existingAcceptedConditions) {
          this.initialData.policyHolder.veteranCard.veteranCardConditionOptions.forEach((initialCondition) => {
            data.forEach((condition) => {
              if (initialCondition.returnValue === condition.returnValue) {
                initialCondition.op = OperationType.NONE;
                condition.op = OperationType.NONE;
              }
            });
          });
        }

        // Check if one or more initial accepted conditions have been de-selected.
        const removedExistingConditions = existingAcceptedConditions.filter((initialCondition) => {
          return !data.some((condition) => {
            return initialCondition.returnValue === condition.returnValue;
          });
        });

        if (removedExistingConditions.length > 0) {
          removedExistingConditions.forEach((removedCondition) => {
            this.initialData.policyHolder.veteranCard.veteranCardConditionOptions.forEach((initialCondition) => {
              if (initialCondition.returnValue === removedCondition.returnValue) {
                initialCondition.op = OperationType.REMOVE;
              }
            });
          });
        }

        data.forEach((condition) => {
          let indexOfExistingCondition = -1; // Default

          if (existingAcceptedConditions) {
            // Check if the current condition is actually an existing condition.
            indexOfExistingCondition = existingAcceptedConditions.findIndex((existingCondition) => {
              return existingCondition.returnValue === condition.returnValue;
            });

            // If it is an existing condition, re-add it to the list of latest accepted conditions using the initial details.
            if (indexOfExistingCondition > -1) {
              acceptedConditions.push({
                id: existingAcceptedConditions[indexOfExistingCondition].id,
                returnValue: existingAcceptedConditions[indexOfExistingCondition].returnValue,
                display: this.getDisplayValue(existingAcceptedConditions[indexOfExistingCondition].returnValue, this.informationReferenceData.veteranCardConditionOptions),
                startDate: existingAcceptedConditions[indexOfExistingCondition].startDate,
                endDate: existingAcceptedConditions[indexOfExistingCondition].originalEndDate,
                originalEndDate: existingAcceptedConditions[indexOfExistingCondition].originalEndDate,
                op: OperationType.NONE,
              });
              this.initialData.policyHolder.veteranCard.veteranCardConditionOptions[indexOfExistingCondition].endDate =
                existingAcceptedConditions[indexOfExistingCondition].originalEndDate;
            }

            // Else, treat this as an entirely new accepted condition.
            if (indexOfExistingCondition === -1) {
              acceptedConditions.push({
                id: null, // New accepted conditions that have not yet been submitted to OHI have no ID.
                returnValue: condition.returnValue,
                display: this.getDisplayValue(condition.returnValue, this.informationReferenceData.veteranCardConditionOptions),
                startDate: getCurrentDate(), // Always set to today's date.
                endDate: null,
                originalEndDate: null,
                op: OperationType.ADD,
              });
            }
          }
        });
      }
      this.policyHolder.veteranCard.veteranCardConditionOptions = acceptedConditions;
    },

    // get the actual display value from the relative api data list
    getDisplayValue(modelValue, listName) {
      let displayValue = '';
      listName.forEach((el) => {
        if (el.returnValue === modelValue) {
          displayValue = el.display;
        }
      });

      return displayValue;
    },

    showConcessionCardSection() {
      const addressState = this.changeCoverData.step1.state;
      const coverType = this.changeCoverData.step2.coverType;
      const extrasOnly = coverType === CoverTypeValue.EXTRAS_ONLY;
      return showConcessionCard(addressState, this.changeCoverData.step1.DOB, extrasOnly);
    },

    showConcessionCardDetailsForm() {
      const addressState = this.changeCoverData.step1.state;
      const coverType = this.changeCoverData.step2.coverType;
      const extrasOnly = coverType === CoverTypeValue.EXTRAS_ONLY;
      return showConcessionCardDetails(addressState, this.changeCoverData.step1.DOB, extrasOnly, this.policyHolder.haveConcessionCard);
    },

    resetConcessionCardDetails() {
      if (this.policyHolder.selectedConcessionCardOptions.length > 0) {
        this.policyHolder.selectedConcessionCardOptions = [{ name: '', number: '', expire: '' }];
      }
    },

    // To add another object for selected contact, concession card, or insurance fund
    addAnotherSelectField(selectedOptions) {
      const firstSelection = JSON.parse(JSON.stringify(selectedOptions[0]));
      Object.keys(firstSelection).forEach((key) => {
        firstSelection[key] = '';
      });

      firstSelection.op = OperationType.ADD;
      firstSelection.startDate = getCurrentDate();
      firstSelection.id = null;

      selectedOptions.push(firstSelection);
    },

    // To remove an object from selected contact, concession card, or insurance fund
    removeSelectField(selectedOptions, index) {
      let selectedData = selectedOptions;
      if (selectedData.length > 1) {
        selectedData = selectedData.splice(index || selectedOptions.length - 1, 1);
      }

      // set to replace in initial data
      this.initialData.policyHolder.selectedConcessionCardOptions.forEach((card) => {
        if (selectedData[0].name === card.name) {
          card.op = OperationType.REMOVE;
        }
      });
    },

    cardDetailUpdated(cardIndex, updateType) {
      const targetCardObj = this.policyHolder.selectedConcessionCardOptions.at(cardIndex);
      const initialCardList = this.initialData.policyHolder.selectedConcessionCardOptions;

      let cardIndexInitialList;
      initialCardList.forEach((card, index) => {
        const sameIds = card.id === targetCardObj.id;
        const sameTypeAndNumber = card.name === targetCardObj.name && card.number === targetCardObj.number;
        if (sameIds || sameTypeAndNumber) {
          cardIndexInitialList = index;
        }
      });

      if (cardIndexInitialList !== undefined) {
        const initialCardObj = initialCardList[cardIndexInitialList];

        switch (updateType) {
          case this.cardDetailUpdatedType.TYPE:
            if (!isEqual(targetCardObj.name, initialCardObj.name)) {
              initialCardObj.op = OperationType.REMOVE;
              targetCardObj.op = OperationType.ADD;
            } else {
              initialCardObj.op = OperationType.NONE;
              targetCardObj.op = OperationType.NONE;

              if (!isEqual(targetCardObj.expire, initialCardObj.expire)) {
                initialCardObj.op = OperationType.NONE;
                targetCardObj.op = OperationType.REPLACE;
              }

              if (!isEqual(targetCardObj.number, initialCardObj.number)) {
                initialCardObj.op = OperationType.REMOVE;
                targetCardObj.op = OperationType.ADD;
              }
            }
            break;

          case this.cardDetailUpdatedType.NUMBER:
            // Only excute following logic when card number input is valid
            if (this.v$.policyHolder.selectedConcessionCardOption.$each.$response?.$data[cardIndex]?.number.$invalid) {
              if (!isEqual(targetCardObj.expire, initialCardObj.expire) && isEqual(targetCardObj.number, initialCardObj.number)) {
                initialCardObj.op = OperationType.NONE;
                targetCardObj.op = OperationType.REPLACE;
              } else if (
                ['date', 'number', 'name'].every((key) => {
                  return targetCardObj[key] === initialCardObj[key];
                })
              ) {
                // if newly input card number is same as initial card number and both expiry date and card type are same
                initialCardObj.op = OperationType.NONE;
                targetCardObj.op = OperationType.NONE;
              } else {
                initialCardObj.op = OperationType.REMOVE;
                targetCardObj.op = OperationType.ADD;
              }
            }
            break;

          case this.cardDetailUpdatedType.EXPIRY_DATE:
            // only update `op` to replace when it is not newly added
            if (targetCardObj.op !== OperationType.ADD && !isEqual(targetCardObj.expire, initialCardObj.expire)) {
              targetCardObj.op = OperationType.REPLACE;
            }
            break;
          default:
            break;
        }

        switch (targetCardObj.op) {
          case OperationType.ADD:
            targetCardObj.startDate = getCurrentDate();
            break;

          default:
            targetCardObj.startDate = initialCardList[cardIndex].startDate;
            break;
        }

        const targetCardToCompare = cloneDeep(targetCardObj);
        const targetCardInitialToCompare = cloneDeep(initialCardObj);

        delete targetCardToCompare.op;
        delete targetCardInitialToCompare.op;

        if (isEqual(targetCardToCompare, targetCardInitialToCompare)) {
          initialCardObj.op = OperationType.NONE;
          targetCardObj.op = OperationType.NONE;
        }

        if (targetCardObj.op === OperationType.ADD) {
          targetCardObj.id = null;
        } else {
          targetCardObj.id = initialCardObj.id;
        }
      }
    },

    updateAddressValidation(data) {
      this.policyHolder.POBox = data.isPOBox;
      this.policyHolder.differentPostalAddress = data.differentPostalAddress;
    },

    updateAddressDetail(addressDetail, type, isInvalid) {
      this.policyHolder.addressesInvalid = isInvalid;
      this.v$.policyHolder.addressesInvalid.$touch();
      this.policyHolder.differentPostalAddress = addressDetail.differentPostalAddress;
      if (type === 'residential') {
        this.policyHolder.residentialAddress = { ...addressDetail.address };
        if (!addressDetail.differentPostalAddress) {
          this.policyHolder.postalAddress = { ...addressDetail.address };
        }
      } else if (type === 'postal') {
        if (addressDetail.differentPostalAddress) {
          this.policyHolder.postalAddress = { ...addressDetail.address };
        } else {
          this.policyHolder.postalAddress = { ...this.policyHolder.residentialAddress };
        }
      }
    },
    getConcessionNumberErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'required')) return 'Please provide a response for Card number.';
      if (errors.some(e => e.$validator === 'invalidConcessionCardNumber')) return 'Please enter 9 numbers and a letter.';
      return '';
    },

    getConcessionExpiryErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'IS_INVALID')) return 'Please enter the expiry date using the format DD/MM/YYYY.';
      if (errors.some(e => e.$validator === 'isPastDate')) return 'Please try again, expiry dates must be a future date.';
      return '';
    },

    async getOverCoverageInfo() {
      this.apiStatus.overCoverage.loading = true;
      this.apiStatus.overCoverage.fail = false;
      this.isPolicyHolderOverCovered = true;

      try {
        const response = await Services.getPersonOverCoverage(this.policyHolder.personCode);
        // overcovered check
        if (response?.duplicateMembers[0]) {
          const coveredMemberships = response.duplicateMembers[0].coveredMemberships;
          const duplicatePersonProducts = [];

          coveredMemberships.forEach((membership) => {
            membership.productsInformation.forEach((productInfo) => {
              duplicatePersonProducts.push({
                code: productInfo.productCode,
                name: productInfo.productName,
                type: productInfo.productTypeDescr,
              });
            });
          });

          this.isPolicyHolderOverCovered = this.overCoverageCheck(duplicatePersonProducts, this.selectedProducts.products);
        } else {
          this.isPolicyHolderOverCovered = false;
        }
      } catch (error) {
        this.apiStatus.overCoverage.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.overCoverage.loading = false;
      }
    },

    showValidConcessionCards() {
      const validConcessionCardTypes = [];

      this.initialData.policyHolder.selectedConcessionCardOptions.forEach((concessionCard) => {
        if (concessionCard.name in ConcessionCardTypes) {
          validConcessionCardTypes.push(concessionCard);
        } else {
          validConcessionCardTypes.push({ name: '', number: '', expire: '' });
        }
      });

      return validConcessionCardTypes;
    },

    reverseFormatFormDate,
  },
};
</script>

<style lang="scss" scoped>
.member-detail {
  width: 100%;

  tr {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  th,
  td {
    border-bottom: none;
    padding-left: 0;
  }
}

.member-details-section {
  padding: 0;
}

.form-section {
  .dropdown-select-wrapper label.dropdown-face {
    height: 37px;
  }

  p:not(.form-section) {
    margin-bottom: 0;
  }

  .field {
    margin-bottom: rem(10);

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.veteran-card-type {
  margin-bottom: 1rem;
}

:deep(section.focusable) {
  & > .separator-wrapper {
    row-gap: 5px;
  }

  .separator-content {
    .form-section:not(:last-child) {
      margin-bottom: rem(25);
    }
  }
}

:deep(.sub-heading-underlined) {
  margin-bottom: 2rem;
}

:deep(.non-button-style) {
  font-weight: $NORMAL;
}
</style>
